<script setup>
import {
  ref,
  reactive,
  computed,
  watch,
  toRefs,
} from 'vue';
import { values, compact } from 'lodash';
import * as utilsUsuarios from '@/libs/usuarios';
import InputCheckboxBoolean from '@/components/cifra/InputCheckboxBoolean.vue';
import SelectorEstructura from '@/components/SelectorEstructura.vue';
import toast from '../../../../../libs/cifra/toast';
import Overlay from '../../../../../libs/cifra/overlay';
import suscripciones from '../../../../../api/cifra/suscripciones';
import IconosTipoUsuario from '../../../../../components/usuarios/IconosTipoUsuario.vue';
import BotonAlumnosCargarExcel from './BotonAlumnosCargarExcel.vue';
import BotonGuardar from '../../../../../components/inputs/BotonGuardar.vue';
import Usuario from '../../../../../models/Usuario';
import SelectorRoles from '../../../../../components/cifra/SelectorRoles.vue';
import { useGeneracionSuscripciones } from './GeneracionMixin';

// Props
const props = defineProps({
  infoServicio: {
    type: Object,
    required: true,
  },
  suscriptores: {
    type: Object,
    required: true,
  },
  urlPlantillaAlumnos: {
    type: String,
    required: true,
  },
});

// Emits
const emit = defineEmits(['tipotransporte-changed']);

// Template refs
const wrapperBusqueda = ref();
const selectorEstructura = ref();
const tablaResultadoBusqueda = ref();

// Make props reactive
const { infoServicio, suscriptores } = toRefs(props);

// Reactive data
const paramsBusqueda = reactive({
  nombre: '',
  roles: [],
});

const alumnosBusqueda = ref({});
const buscando = ref(false);
const verTodoTransporte = ref(false);
const tipoTransporte = ref('fijos');

// Use composable for GeneracionMixin functionality
const {
  numeroSuscriptores,
  esTransporte,
  esMensual,
  nombreGrupo,
} = useGeneracionSuscripciones(infoServicio, suscriptores);

// Component computed properties
const alumnosBusquedaVisibles = computed(() => {
  let visibles = values(alumnosBusqueda.value);
  const idsSuscritos = Object.keys(suscriptores.value);
  visibles = visibles.filter((usu) => !idsSuscritos.includes(usu.id.toString()));

  return utilsUsuarios.ordenar(visibles);
});

const esTransportePagoUnico = computed(() => (
  esTransporte.value && !esMensual.value
));

// Component methods that use nombreGrupo from composable
const asignarAlumnosBusqueda = (alumnos) => {
  alumnosBusqueda.value = {};
  alumnos.forEach((alumno) => {
    alumno.grupo = nombreGrupo(alumno);
    alumnosBusqueda.value[alumno.id] = new Usuario(alumno);
  });
};

// Component methods
const limpiarSeleccionados = () => {
  alumnosBusqueda.value = {};
  Object.keys(suscriptores.value).forEach((id) => {
    delete suscriptores.value[id];
  });
};

const buscarAlumnos = () => {
  buscando.value = true;
  const roles = paramsBusqueda.roles.length
    ? paramsBusqueda.roles.map((r) => r.id)
    : undefined;
  const params = {
    nombre: paramsBusqueda.nombre,
    // undefined para que no lo cuente como filtro debajo y de error si no hay filtros
    rol_id: roles,
    transporte: esTransporte.value ? true : undefined,
    tipo_transporte: esTransportePagoUnico.value ? tipoTransporte.value : undefined,
    ver_todo_transporte: verTodoTransporte.value ? true : undefined,
  };

  const hashPathEstructura = selectorEstructura.value.getPathSeleccionadoEnHash({
    soloTiposPermitidos: true,
  });

  Object.keys(hashPathEstructura).forEach((tipo) => {
    params[`${tipo}_id`] = hashPathEstructura[tipo].id;
  });

  if (!compact(values(params)).length) {
    toast.info('error_filtros_vacios'); // Using translation key directly
    alumnosBusqueda.value = {};
    buscando.value = false;
    return;
  }
  const overlay = new Overlay(wrapperBusqueda.value);
  overlay.show();
  suscripciones.buscarAlumnos({ params }).then((result) => {
    asignarAlumnosBusqueda(result.data);
    buscando.value = false;
    overlay.hide();
  }).catch(() => {
    overlay.hide();
    buscando.value = false;
  });
};

const excelCargada = (alumnos) => {
  asignarAlumnosBusqueda(alumnos);
};

const marcarTodaBusqueda = () => {
  const marcar = tablaResultadoBusqueda.value
    .querySelectorAll('.check-alumno:not(:checked)').length > 0;
  /* eslint no-param-reassign: ["error", { "props": false }] */
  tablaResultadoBusqueda.value.querySelectorAll('.check-alumno').forEach((check) => {
    check.checked = marcar;
  });
};

const anadirSuscriptores = () => {
  const anadidos = [];
  tablaResultadoBusqueda.value.querySelectorAll('.check-alumno:checked').forEach((el) => {
    const suscriptor = alumnosBusqueda.value[el.value];
    suscriptor.numero_servicios = 1;
    suscriptores.value[suscriptor.id] = new Usuario(suscriptor);
    anadidos.push(suscriptor.id);
  });
};

// Watchers
watch(
  tipoTransporte,
  (value) => {
    limpiarSeleccionados();
    emit('tipotransporte-changed', value);
  },
);

watch(
  esTransportePagoUnico,
  () => {
    limpiarSeleccionados();
  },
);
</script>

<template>
  <div id="seleccion-suscriptores-wrapper">
    <div ref="wrapperBusqueda" class="card bg-secondary m-t">
      <div class="row">
        <div class="col-md-6">
          <div class="card-header border-bottom-cifra">
            {{ $I18n.t('buscar_usuario') }}
          </div>
          <div ref="wrapperBusqueda" class="card-body card-busqueda">
            <form autocomplete="off" @submit.prevent="buscarAlumnos">
              <div class="row">
                <div class="col-md-12">
                  <div class="form-group">
                    <label class="control-label">{{ $I18n.t('Nombre') }}</label>
                    <input
                      v-model="paramsBusqueda.nombre"
                      type="text"
                      class="form-control string required"
                    >
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-12">
                  <div class="form-group">
                    <label class="control-label">{{ $I18n.t('roles') }}</label>
                    <SelectorRoles
                      v-model="paramsBusqueda.roles"
                      style="z-index: 1001;"
                    >
                    </SelectorRoles>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-12">
                  <div class="form-group">
                    <label class="control-label">{{ $I18n.t('curso_alumnos') }}</label>
                    <SelectorEstructura
                      ref="selectorEstructura"
                      :tipos-seleccionables="['ensennanza', 'nivel', 'curso', 'modalidad', 'grupo']"
                      :namespace-parametros="false"
                      :small="true"
                    ></SelectorEstructura>
                  </div>
                </div>
              </div>
              <div class="form-actions">
                <div v-if="esTransportePagoUnico" class="form-inline">
                  <select
                    v-model="tipoTransporte"
                    :disabled="numeroSuscriptores > 0"
                    class="form-control pull-left"
                  >
                    <option value="fijos" selected>
                      {{ $I18n.t('fijos') }}
                    </option>
                    <option value="eventuales">
                      {{ $I18n.t('eventuales') }}
                    </option>
                  </select>
                </div>
                <div v-if="esTransporte" class="form-inline pull-left m-l">
                  <InputCheckboxBoolean
                    v-model="verTodoTransporte"
                    tipo="switch"
                    class="m-t-sm"
                  >
                    {{ $t('ver_todo') }}
                  </InputCheckboxBoolean>
                </div>
                <BotonAlumnosCargarExcel
                  class="m-r-xs"
                  :url-plantilla-alumnos="urlPlantillaAlumnos"
                  @cargada="excelCargada"
                />
                <BotonGuardar :submitting="buscando" action="buscar"></BotonGuardar>
              </div>
            </form>
          </div>
        </div>
        <div v-if="alumnosBusquedaVisibles.length" class="col-md-6">
          <div class="card-body">
            <div class="datatable-v-scroll datatable-h-7">
              <table
                ref="tablaResultadoBusqueda"
                class="table table-striped cifra-table table-form m-t-none"
              >
                <thead>
                  <tr>
                    <th class="col-min"></th>
                    <th>{{ $I18n.t('Nombre') }}</th>
                    <th>{{ $I18n.t('Grupo') }}</th>
                    <th class="nosort col-icon-1">
                      <button
                        type="button"
                        class="btn btn-xs btn-default as-check"
                        @click="marcarTodaBusqueda"
                      >
                        <i class="far fa-check-square"></i>
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="usuario in alumnosBusquedaVisibles" :key="usuario.id">
                    <td class="nowrap">
                      <IconosTipoUsuario :usuario="usuario"></IconosTipoUsuario>
                    </td>
                    <td>{{ usuario.nombreCompleto }}</td>
                    <td>{{ usuario.grupo }}</td>
                    <td>
                      <div class="pretty_checkbox">
                        <div class="pretty p-icon">
                          <input
                            class="check-alumno pretty_checkbox"
                            type="checkbox"
                            :value="usuario.id"
                          >
                          <div class="state p-cifra">
                            <i class="icon fa fa-check"></i>
                            <label class="pretty_checkbox control-label"></label>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="form-actions m-t">
              <button
                v-show="Object.keys(alumnosBusqueda).length"
                class="btn btn-cifra"
                @click="anadirSuscriptores"
              >
                {{ $I18n.t('Anadir') }} <i class="fa fa-arrow-down"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Para que no haga scroll y se vea bien el selector de estructura */
.card-busqueda {
  overflow: visible;
}
</style>
